import { jsPDF } from "jspdf";

export default function generateShuttleReport(data, selectedDate = null) {
  const doc = new jsPDF('landscape');

  // Use the selected date if provided, otherwise try to extract from data, otherwise use current date
  let formattedDate;
  if (selectedDate) {
    formattedDate = selectedDate;
  } else {
    let latestReportDate = null;
    data.forEach(shuttle => {
      if (shuttle.reportDate) {
        const currentDate = new Date(shuttle.reportDate);
        if (!latestReportDate || currentDate > latestReportDate) {
          latestReportDate = currentDate;
        }
      }
    });
    formattedDate = latestReportDate ? latestReportDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
  }

  // Column definitions for consistent layout (defined early to avoid hoisting issues)
  const columns = [
    { header: "First Name", width: 30, key: 'firstname' },
    { header: "Last Name", width: 30, key: 'lastname' },
    { header: "Phone", width: 25, key: 'phone' },
    { header: "Email", width: 45, key: 'email' },
    { header: "Shuttle Name", width: 20, key: 'shuttleName' },
    { header: "Pickup Time", width: 20, key: 'flight_time' },
    { header: "Pickup Location", width: 50, key: 'pickup_location' },
    { header: "Arrival Time", width: 20, key: 'arrival_time' },
    { header: "Dropoff Location", width: 30, key: 'dropoff_location' },
  ];

  // Function to wrap text within a column width (defined early to avoid hoisting issues)
  const wrapText = (doc, text, maxWidth) => {
    if (!text) return [''];

    const words = text.toString().split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = doc.getTextWidth(testLine);

      if (textWidth <= maxWidth - 4) { // Leave 4 units padding
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, truncate it
          lines.push(word.substring(0, Math.floor(word.length * maxWidth / textWidth)));
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
  };

  // Function to draw text in a cell with proper boundaries (defined early to avoid hoisting issues)
  const drawCellText = (doc, text, x, y, width, height) => {
    const lines = wrapText(doc, text, width);
    const lineHeight = 4;

    // Draw each line of text
    lines.forEach((line, index) => {
      if (index * lineHeight < height - 2) { // Make sure text fits in cell height
        doc.text(line, x + 2, y + 4 + (index * lineHeight));
      }
    });

    return lines.length * lineHeight;
  };

  // Adding a cover page
  /*
  const addCoverPage = (doc, shuttleNames) => {
    doc.setFontSize(20);
    doc.text("Shuttle Report", 10, 20);
    doc.setFontSize(16);
    doc.text(`Report Date: ${formattedDate}`, 10, 30);

    const summaries = [
      {
        title: 'Shuttle Breakdown',
        details: `Shuttles: ${shuttleNames.join(', ')}`
      }
    ];

    let y = 50;
    summaries.forEach(summary => {
      doc.setFontSize(12);
      doc.text(summary.title, 10, y);
      doc.setFontSize(10);
      doc.text(summary.details, 10, y + 10);
      y += 30;
    });

    doc.addPage('landscape'); // Add a new page for the detailed reports
  };
  */

  // Function to draw table headers using the column system
  const drawHeaders = (doc, y) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');

    let x = 10;
    columns.forEach(col => {
      doc.setDrawColor(0);
      doc.setFillColor(211, 211, 211);
      doc.rect(x, y, col.width, 8, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.text(col.header, x + 2, y + 6);
      x += col.width;
    });

    doc.setFont('helvetica', 'normal');
    return y + 8;
  };

  // Function to draw page header
  const drawPageHeader = (doc, shuttleName, formattedDate) => {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Shuttle Name: ${shuttleName}`, 10, 10);
    doc.text(`Report Date: ${formattedDate}`, 225, 10);
    return 20; // Return Y position after header
  };

  // Filter out empty or null shuttle names and create a unique list of shuttle names
  const shuttleNames = [...new Set(data
    .filter(item => item.shuttleName && item.shuttleName.trim() !== '')
    .map(item => item.shuttleName)
  )];

  // Pass this data to the cover page function
  //addCoverPage(doc, shuttleNames);

  shuttleNames.forEach((shuttleName, index) => {
    if (index > 0) doc.addPage('landscape');

    let y = drawPageHeader(doc, shuttleName, formattedDate);
    const shuttleData = data.filter(item => item.shuttleName === shuttleName);

    y = drawHeaders(doc, y);
    y += 2;

    shuttleData.forEach(item => {
      doc.setFontSize(9);

      // Calculate the row height needed based on text wrapping
      let maxRowHeight = 8; // Minimum row height

      // First pass: calculate required height for this row
      columns.forEach(col => {
        let cellText = '';
        if (col.key === 'firstname') {
          cellText = item.firstname || 'N/A';
        } else if (col.key === 'lastname') {
          cellText = item.lastname || 'N/A';
        } else if (col.key === 'phone') {
          cellText = item.phone || 'N/A';
        } else if (col.key === 'email') {
          cellText = item.email || 'N/A';
        } else if (col.key === 'shuttleName') {
          cellText = item.shuttleName || 'N/A';
        } else if (col.key === 'flight_time') {
          cellText = item.flight_time || 'N/A';
        } else if (col.key === 'pickup_location') {
          cellText = item.pickup_location || 'N/A';
        } else if (col.key === 'arrival_time') {
          cellText = item.arrival_time || 'N/A';
        } else if (col.key === 'dropoff_location') {
          cellText = item.dropoff_location || 'N/A';
        }

        const lines = wrapText(doc, cellText, col.width);
        const requiredHeight = Math.max(8, lines.length * 4 + 2);
        maxRowHeight = Math.max(maxRowHeight, requiredHeight);
      });

      // Check if we need a new page for this row
      if (y + maxRowHeight > 190) {
        doc.addPage('landscape');
        y = drawPageHeader(doc, shuttleName, formattedDate);
        y = drawHeaders(doc, y);
        y += 2;
      }

      // Draw cell borders and content
      let x = 10;
      columns.forEach(col => {
        // Draw cell border
        doc.setDrawColor(200, 200, 200);
        doc.rect(x, y, col.width, maxRowHeight);

        // Get cell content
        let cellText = '';
        if (col.key === 'firstname') {
          cellText = item.firstname || 'N/A';
        } else if (col.key === 'lastname') {
          cellText = item.lastname || 'N/A';
        } else if (col.key === 'phone') {
          cellText = item.phone || 'N/A';
        } else if (col.key === 'email') {
          cellText = item.email || 'N/A';
        } else if (col.key === 'shuttleName') {
          cellText = item.shuttleName || 'N/A';
        } else if (col.key === 'flight_time') {
          cellText = item.flight_time || 'N/A';
        } else if (col.key === 'pickup_location') {
          cellText = item.pickup_location || 'N/A';
        } else if (col.key === 'arrival_time') {
          cellText = item.arrival_time || 'N/A';
        } else if (col.key === 'dropoff_location') {
          cellText = item.dropoff_location || 'N/A';
        }

        // Set text color
        doc.setTextColor(0, 0, 0);

        // Draw cell content
        drawCellText(doc, cellText, x, y, col.width, maxRowHeight);

        x += col.width;
      });

      y += maxRowHeight;
    });
  });

  //const today = new Date();
  //const dateString = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
  doc.save(`shuttle-report-${formattedDate}.pdf`);
}
